/**
 * Presentation-only choreography for the card-duel arena.
 *
 * The server sends a monotonically ordered semantic beat after it accepts an attack.
 * This module consumes each beat once, resolves the same baked UO clips used by the world,
 * and owns all temporary movement/projectile DOM. The gump only calls {@link observe} after
 * rendering authoritative state, which keeps game rules and animation timing independent.
 */
import {
  cardBattleSpriteEnvelope,
  cardBattleSpriteFrames,
  cachedCardBattleSpriteFrames,
  cardActionArt,
  cardEffectSpriteClip,
  cardStandSpriteFrames,
  paintHuedCardSprite,
  type CardBattleSpriteArt,
  type CardBattleSpriteEnvelope,
} from './cardArt';
import type {
  CardDuelStateMessage,
  WireCardBattleAttackBeat,
  WireCardBattleBeat,
  WireCardBattleEffectBeat,
  WireCardBattlePlayBeat,
  WireCardBattleTarget,
} from './net/protocol';
import { cardDef, isKnownCard, monsterById } from './sim/index';
import { cardPresentationProfile } from './cardBattlePresentation';
import type { SoundEvent } from './audio/soundObserver';
import {
  CARD_ATTACK_VOLUME,
  cardEffectCue,
  cardMonsterCue,
  type CardBattleSoundIdentity,
} from './audio/cardBattleCue';

const FRAME_MS = 45;
const ADVANCE_MS = 135;
const IMPACT_MS = 90;
const RETURN_MS = 120;
const FIRE_BREATH_TRAVEL_MS = 380;
const CAST_MS = 180;
const EFFECT_MS = 220;
const MAX_QUEUED_BEATS = 48;
const MAX_ACTION_FRAMES = 5;
const NORTH_DIRECTION = 4;
const SOUTH_DIRECTION = 0;
/**
 * Battlefield sprite sizing: every creature renders at the same uniform multiple of
 * its native UO pixels, so a slime stays visibly smaller than an ettin - the same
 * relative sizes the world renderer shows. The envelope below is a safety clamp
 * only: it shrinks the rare giant whose native crop would not fit an arena row,
 * never inflates a small body.
 */
export const MAX_BATTLE_SPRITE_WIDTH = 110;
export const MAX_BATTLE_SPRITE_HEIGHT = 135;
export const BATTLE_SPRITE_UNIFORM_SCALE = 1.25;
const battleSpriteScaleCache = new Map<string, number>();

type ArenaSide = 'you' | 'foe';

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function selectorForInstance(instanceId: number): string {
  return `[data-instance-id="${instanceId}"]`;
}

function setMotionVector(actor: HTMLElement, target: HTMLElement): void {
  const from = actor.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  actor.style.setProperty('--battle-travel-x', `${dx * 0.72}px`);
  actor.style.setProperty('--battle-travel-y', `${dy * 0.72}px`);
}

function cropPosition(offset: number, atlasSize: number, cropSize: number): number {
  const travel = atlasSize - cropSize;
  return travel <= 0 ? 0 : offset / travel * 100;
}

/** The uniform battlefield scale for one clip family, reduced only if its silhouette envelope overflows an arena row. */
export function cardBattleSpriteScale(
  frames: ReadonlyArray<Pick<CardBattleSpriteArt, 'width' | 'height' | 'cx' | 'cy'>>,
): number {
  if (frames.length === 0) return 1;
  return cardBattleSpriteEnvelopeScale({
    horizontalExtent: Math.max(...frames.map((frame) => Math.max(frame.cx, frame.width - frame.cx))),
    extentAboveGround: Math.max(...frames.map((frame) => frame.height + frame.cy)),
    extentBelowGround: Math.max(0, ...frames.map((frame) => -frame.cy)),
  });
}

function cardBattleSpriteEnvelopeScale(envelope: CardBattleSpriteEnvelope): number {
  const verticalExtent = envelope.extentAboveGround + envelope.extentBelowGround;
  const { horizontalExtent } = envelope;
  if (horizontalExtent <= 0 || verticalExtent <= 0) return 1;
  return Math.min(
    BATTLE_SPRITE_UNIFORM_SCALE,
    (MAX_BATTLE_SPRITE_WIDTH / 2) / horizontalExtent,
    MAX_BATTLE_SPRITE_HEIGHT / verticalExtent,
  );
}

function bodySpriteScale(body: number, direction: number): number {
  const key = `${body}:${direction}`;
  const cached = battleSpriteScaleCache.get(key);
  if (cached !== undefined) return cached;
  const envelope = cardBattleSpriteEnvelope(body, direction);
  const scale = envelope
    ? cardBattleSpriteEnvelopeScale(envelope)
    : cardBattleSpriteScale(cardStandSpriteFrames(body, direction));
  battleSpriteScaleCache.set(key, scale);
  return scale;
}

/** Render and play accepted card actions without leaking animation state into the gump. */
export class CardBattleAnimator {
  private matchId: number | undefined;
  private lastSequence: number | undefined;
  private queue: Array<{ state: CardDuelStateMessage; beat: WireCardBattleBeat }> = [];
  private playing = false;
  private generation = 0;
  /** Card identity survives authoritative removal until its queued death cue plays. */
  private identities = new Map<number, CardBattleSoundIdentity>();
  /** Signature spell sounds already emitted for the current accepted action. */
  private soundedSignatures = new Set<string>();
  /** Vanishing actors cloned before the redraw, with the board slot each stood in. */
  private stagedGhosts = new Map<number, {
    ghost: HTMLElement;
    boardIndex: number;
    side: ArenaSide;
  }>();
  private idleResolvers: Array<() => void> = [];

  constructor(
    private readonly doc: Document,
    private readonly battlefield: HTMLElement,
    private readonly onWorldBeat?: (state: CardDuelStateMessage, beat: WireCardBattleBeat) => void,
    private readonly onSound?: (cue: SoundEvent) => void,
  ) {}

  /**
   * Capture actors that the next authoritative state removes before the gump redraws.
   * A lethal exchange can remove either participant from both boards, but its accepted
   * beat must still have pixels to animate.
   */
  prepare(state: CardDuelStateMessage): void {
    this.switchMatch(state.matchId);
    this.rememberCards(state);
    if (this.lastSequence === undefined) return;
    const present = new Set(
      [...state.you.board, ...state.foe.board].map((card) => card.instanceId),
    );
    for (const beat of state.beats) {
      if (beat.sequence <= this.lastSequence) continue;
      if (beat.kind === 'attack') {
        if (!present.has(beat.attackerId)) this.stageActor(beat.attackerId);
        if (beat.target.kind === 'minion' && !present.has(beat.target.instanceId)) {
          this.stageActor(beat.target.instanceId);
        }
      } else if (beat.kind === 'effect') {
        if (
          (beat.effect === 'death' || beat.effect === 'return-to-hand')
          && beat.instanceId !== undefined
          && !present.has(beat.instanceId)
        ) this.stageActor(beat.instanceId);
        if (beat.target?.kind === 'minion' && !present.has(beat.target.instanceId)) {
          this.stageActor(beat.target.instanceId);
        }
      }
    }
  }

  /** Refresh resting facings and enqueue any server beats not previously observed. */
  observe(state: CardDuelStateMessage): void {
    this.switchMatch(state.matchId);
    this.rememberCards(state);
    this.paintRestingArmies(state);
    const latest = state.beats.at(-1)?.sequence ?? 0;
    if (this.lastSequence === undefined) {
      this.lastSequence = latest;
      return;
    }

    const unseen = state.beats.filter((beat) => beat.sequence > this.lastSequence!);
    this.lastSequence = Math.max(this.lastSequence, latest);
    for (const beat of unseen) this.queue.push({ state, beat });
    this.compactQueue();
    void this.drain();
  }

  /**
   * Keep bursty turns within a presentation budget by dropping oldest low-value
   * narration first. Plays, impacts, summons, deaths, delayed signatures, and
   * settlement are never silently compacted.
   */
  private compactQueue(): void {
    if (this.queue.length <= MAX_QUEUED_BEATS) return;
    const expendable = (beat: WireCardBattleBeat): boolean =>
      beat.kind === 'effect'
      && (
        beat.effect === 'draw'
        || beat.effect === 'burn'
        || beat.effect === 'ability'
        || beat.effect === 'turn-start'
        || beat.effect === 'buff'
      );
    for (let i = 0; i < this.queue.length && this.queue.length > MAX_QUEUED_BEATS;) {
      if (expendable(this.queue[i]!.beat)) this.queue.splice(i, 1);
      else i += 1;
    }
  }

  /** Cancel queued presentation and forget the match cursor. */
  reset(): void {
    this.matchId = undefined;
    this.cancelPresentation();
  }

  /** Whether every accepted beat observed so far has finished presenting. */
  isIdle(): boolean {
    return !this.playing && this.queue.length === 0;
  }

  /** Resolve once every accepted beat observed so far has finished presenting. */
  whenIdle(): Promise<void> {
    if (this.isIdle()) return Promise.resolve();
    return new Promise((resolve) => this.idleResolvers.push(resolve));
  }

  private switchMatch(matchId: number): void {
    if (this.matchId === matchId) return;
    this.cancelPresentation();
    this.matchId = matchId;
    this.identities.clear();
  }

  private cancelPresentation(): void {
    this.generation += 1;
    this.lastSequence = undefined;
    this.queue = [];
    this.playing = false;
    this.soundedSignatures.clear();
    this.stagedGhosts.clear();
    this.battlefield.querySelectorAll('.card-battle-fire-breath, .card-battle-effect, .card-battle-spell-fx, [data-battle-ghost="true"]')
      .forEach((node) => node.remove());
    this.battlefield.querySelectorAll<HTMLElement>('[data-battle-retain="true"]')
      .forEach((node) => {
        delete node.dataset.battleRetain;
        node.classList.remove(
          'card-battle-advancing',
          'card-battle-striking',
          'card-battle-returning',
          'card-battle-breathing',
          'card-battle-hit',
          'card-battle-casting',
          'card-battle-materializing',
          'card-battle-vanishing',
        );
      });
    this.resolveIdle();
  }

  private resolveIdle(): void {
    const resolvers = this.idleResolvers.splice(0);
    for (const resolve of resolvers) resolve();
  }

  private paintRestingArmies(state: CardDuelStateMessage): void {
    this.paintSide(state.you.board, 'you', NORTH_DIRECTION);
    this.paintSide(state.foe.board, 'foe', SOUTH_DIRECTION);
  }

  private paintSide(
    cards: CardDuelStateMessage['you']['board'],
    side: ArenaSide,
    direction: number,
  ): void {
    for (const card of cards) {
      const actor = this.battlefield.querySelector<HTMLElement>(selectorForInstance(card.instanceId));
      if (!actor || actor.dataset.side !== side || !isKnownCard(card.cardId)) continue;
      const def = cardDef(card.cardId, card.rarity);
      if (def.kind !== 'monster') continue;
      const frames = cardStandSpriteFrames(def.body, direction);
      this.paintFrame(actor, frames[0], def.hue ?? 0, bodySpriteScale(def.body, direction));
      void Promise.all((['walk', 'attack-a', 'attack-b', 'attack-c'] as const)
        .map((action) => cardBattleSpriteFrames(def.body, action, direction)));
    }
  }

  private async drain(): Promise<void> {
    if (this.playing) return;
    this.playing = true;
    const generation = this.generation;
    while (this.queue.length > 0 && generation === this.generation) {
      const next = this.queue.shift();
      if (!next) continue;
      if (next.beat.kind === 'attack') {
        this.soundedSignatures.clear();
        this.onWorldBeat?.(next.state, next.beat);
        await this.playAttack(next.state, next.beat, generation);
      } else if (next.beat.kind === 'play') {
        this.soundedSignatures.clear();
        await this.playCard(next.state, next.beat, generation);
      } else {
        if (next.beat.effect === 'turn-start') this.soundedSignatures.clear();
        await this.playEffect(next.state, next.beat, generation);
      }
    }
    if (generation === this.generation) {
      this.playing = false;
      this.resolveIdle();
    }
  }

  private async playCard(
    state: CardDuelStateMessage,
    beat: WireCardBattlePlayBeat,
    generation: number,
  ): Promise<void> {
    this.onWorldBeat?.(state, beat);
    if (beat.cardKind === 'monster') return;
    const side: ArenaSide = beat.seat === state.yourSeat ? 'you' : 'foe';
    const hero = this.battlefield.querySelector<HTMLElement>(
      `.card-battle-${side} .card-battle-hero`,
    );
    if (!hero) return;
    hero.classList.add('card-battle-casting');
    await wait(CAST_MS);
    if (generation === this.generation) hero.classList.remove('card-battle-casting');
  }

  private async playEffect(
    state: CardDuelStateMessage,
    beat: WireCardBattleEffectBeat,
    generation: number,
  ): Promise<void> {
    this.onWorldBeat?.(state, beat);
    this.playEffectSounds(beat);
    const sideForSeat = (seat: 0 | 1 | undefined): ArenaSide =>
      seat === state.yourSeat ? 'you' : 'foe';
    let target: HTMLElement | null = null;
    let visualEffect: string | undefined;
    if (beat.target) target = this.elementForTarget(state, beat.target);
    if (!target && beat.instanceId !== undefined) {
      target = this.actorElement(beat.instanceId);
    }
    if (!target && beat.seat !== undefined) {
      target = this.battlefield.querySelector(`.card-battle-${sideForSeat(beat.seat)} .card-battle-hero`);
    }

    if (beat.effect === 'summon' && target) {
      target.classList.add('card-battle-materializing');
      this.addEffect(target, '✦', 0x80b7ff);
    } else if ((beat.effect === 'death' || beat.effect === 'return-to-hand') && target) {
      target.classList.add('card-battle-vanishing');
      this.addEffect(target, beat.effect === 'death' ? '☠' : '↥', 0xb9b1a3);
    } else if (beat.effect === 'damage' && target) {
      const profile = cardPresentationProfile(beat.sourceCardId);
      target.classList.add('card-battle-hit');
      this.addEffect(target, `−${beat.amount ?? 0}`, profile?.color ?? 0xe2534a, profile?.impact);
      visualEffect = profile?.impact;
    } else if (beat.effect === 'heal' && target) {
      this.addEffect(target, `+${beat.amount ?? 0}`, 0x62d66f, 'greater-heal');
      visualEffect = 'greater-heal';
    } else if (beat.effect === 'buff' && target) {
      this.addEffect(target, `+${beat.attack ?? 0}/+${beat.health ?? 0}`, 0xe5d28b);
    } else if (beat.effect === 'status' && target) {
      this.addEffect(target, beat.action === 'apply' ? 'Paralyzed' : 'Released', 0x8fcfff);
    } else if (beat.effect === 'ward' && target) {
      this.addEffect(target, beat.action === 'gain' ? 'Ward' : 'Absorbed', 0xe5d28b);
    } else if (beat.effect === 'fatigue' && target) {
      this.addEffect(target, `Fatigue −${beat.amount ?? 0}`, 0xe2534a);
    } else if (beat.effect === 'delayed-damage' && target) {
      this.addEffect(
        target,
        beat.action === 'arm' ? 'Explosion armed' : beat.action === 'fizzle' ? 'Fizzled' : 'Explosion',
        0xff713c,
        beat.action === 'detonate' ? 'explosion' : undefined,
      );
      if (beat.action === 'detonate') visualEffect = 'explosion';
    } else if (beat.effect === 'fizzle') {
      const sourceSide = sideForSeat(beat.sourceSeat ?? beat.seat);
      const source = this.battlefield.querySelector<HTMLElement>(
        `.card-battle-${sourceSide} .card-battle-hero`,
      );
      if (source) this.addEffect(source, 'Fizzles', 0xb9b1a3);
    }

    if (!target && beat.effect !== 'fizzle') return;
    await Promise.all([
      wait(EFFECT_MS),
      target && visualEffect
        ? this.playSpellEffect(target, visualEffect, generation)
        : Promise.resolve(),
    ]);
    if (generation !== this.generation) return;
    target?.classList.remove(
      'card-battle-hit',
      'card-battle-materializing',
      'card-battle-vanishing',
    );
    if (target?.dataset.battleGhost === 'true') this.release(target);
  }

  private async playSpellEffect(
    target: HTMLElement,
    effectId: string,
    generation: number,
  ): Promise<void> {
    const clip = cardEffectSpriteClip(effectId);
    if (!clip) return;
    const sprite = this.doc.createElement('span');
    sprite.className = 'card-battle-spell-fx';
    sprite.dataset.effect = effectId;
    target.appendChild(sprite);
    try {
      for (const frame of clip.frames) {
        if (generation !== this.generation || !sprite.isConnected) return;
        sprite.style.width = `${frame.width}px`;
        sprite.style.height = `${frame.height}px`;
        sprite.style.marginLeft = `${-frame.width / 2}px`;
        sprite.style.marginTop = `${-frame.height / 2}px`;
        sprite.style.backgroundImage = `url("${frame.atlasUrl}")`;
        sprite.style.backgroundPosition = `${cropPosition(frame.x, frame.atlasWidth, frame.width)}% ${cropPosition(frame.y, frame.atlasHeight, frame.height)}%`;
        sprite.style.backgroundSize = `${frame.atlasWidth / frame.width * 100}% ${frame.atlasHeight / frame.height * 100}%`;
        await wait(clip.frameIntervalMs);
      }
    } finally {
      sprite.remove();
    }
  }

  private elementForTarget(
    state: CardDuelStateMessage,
    target: WireCardBattleTarget,
  ): HTMLElement | null {
    if (target.kind === 'hero') {
      const side: ArenaSide = target.seat === state.yourSeat ? 'you' : 'foe';
      return this.battlefield.querySelector(`.card-battle-${side} .card-battle-hero`);
    }
    return this.actorElement(target.instanceId);
  }

  private addEffect(
    target: HTMLElement,
    text: string,
    color: number,
    impact?: string,
  ): void {
    const effect = this.doc.createElement('span');
    effect.className = 'card-battle-effect';
    effect.textContent = text;
    effect.style.color = `#${color.toString(16).padStart(6, '0')}`;
    if (impact) effect.dataset.impact = impact;
    target.appendChild(effect);
    setTimeout(() => effect.remove(), EFFECT_MS);
  }

  private async playAttack(
    state: CardDuelStateMessage,
    beat: WireCardBattleAttackBeat,
    generation: number,
  ): Promise<void> {
    const actor = this.actorElement(beat.attackerId);
    const target = this.targetElement(state, beat);
    if (!actor || !target || !isKnownCard(beat.attackerCardId)) return;
    const def = cardDef(beat.attackerCardId, beat.attackerRarity);
    if (def.kind !== 'monster') return;

    const yourAttack = beat.seat === state.yourSeat;
    const direction = yourAttack ? NORTH_DIRECTION : SOUTH_DIRECTION;
    const hue = def.hue ?? 0;
    const scale = bodySpriteScale(def.body, direction);
    const stand = cardStandSpriteFrames(def.body, direction);
    const walk = cachedCardBattleSpriteFrames(def.body, 'walk', direction);
    const attack = this.attackFrames(def.body, direction);
    const breathesFire = monsterById(def.cardId)?.lesserSlayers?.includes('dragon') ?? false;
    this.retain(actor, target);

    if (breathesFire) {
      let projectile: HTMLElement | undefined;
      try {
        this.playMonsterSound(
          { cardId: beat.attackerCardId, rarity: beat.attackerRarity },
          'attack',
          CARD_ATTACK_VOLUME,
        );
        actor.classList.add('card-battle-breathing');
        projectile = this.launchFireBreath(actor, target);
        const breathFrames = attack.length > 0 ? attack : stand;
        await this.playFrames(actor, breathFrames, hue, scale, generation);
        if (generation !== this.generation) return;
        await wait(Math.max(0, FIRE_BREATH_TRAVEL_MS - breathFrames.length * FRAME_MS));
        if (generation !== this.generation) return;
        target.classList.add('card-battle-hit');
        await wait(IMPACT_MS);
        if (generation !== this.generation) return;
        this.paintFrame(actor, stand[0], hue, scale);
      } finally {
        projectile?.remove();
        if (generation === this.generation) {
          target.classList.remove('card-battle-hit');
          actor.classList.remove('card-battle-breathing');
          this.release(actor, target);
        }
      }
      return;
    }

    try {
      setMotionVector(actor, target);
      actor.classList.add('card-battle-advancing');
      await this.playTravelFrames(actor, walk.length > 0 ? walk : stand, hue, scale, generation);
      if (generation !== this.generation) return;
      actor.classList.add('card-battle-striking');
      this.playMonsterSound(
        { cardId: beat.attackerCardId, rarity: beat.attackerRarity },
        'attack',
        CARD_ATTACK_VOLUME,
      );
      await this.playFrames(actor, attack.length > 0 ? attack : stand, hue, scale, generation);
      if (generation !== this.generation) return;
      target.classList.add('card-battle-hit');
      await wait(IMPACT_MS);
      if (generation !== this.generation) return;
      target.classList.remove('card-battle-hit');
      actor.classList.remove('card-battle-advancing', 'card-battle-striking');
      actor.classList.add('card-battle-returning');
      await wait(RETURN_MS);
      if (generation !== this.generation) return;
      actor.classList.remove('card-battle-returning');
      this.paintFrame(actor, stand[0], hue, scale);
    } finally {
      if (generation === this.generation) {
        target.classList.remove('card-battle-hit');
        actor.classList.remove(
          'card-battle-advancing',
          'card-battle-striking',
          'card-battle-returning',
        );
        this.release(actor, target);
      }
    }
  }

  private targetElement(
    state: CardDuelStateMessage,
    beat: WireCardBattleAttackBeat,
  ): HTMLElement | null {
    if (beat.target.kind === 'minion') {
      return this.actorElement(beat.target.instanceId);
    }
    const side = beat.target.seat === state.yourSeat ? 'you' : 'foe';
    return this.battlefield.querySelector(`.card-battle-${side} .card-battle-hero`);
  }

  private actorElement(instanceId: number): HTMLElement | null {
    const existing = this.battlefield.querySelector<HTMLElement>(selectorForInstance(instanceId));
    if (existing) return existing;
    const staged = this.stagedGhosts.get(instanceId);
    if (!staged) return null;
    const board = this.battlefield.querySelector<HTMLElement>(
      `.card-battle-${staged.side}-board`,
    );
    if (!board) return null;
    this.stagedGhosts.delete(instanceId);
    const { ghost, boardIndex } = staged;
    ghost.dataset.battleGhost = 'true';
    ghost.dataset.side = staged.side;
    // Rejoin the row at the slot the minion died in, so the board never reorders
    // mid-animation (a far-left victim must not jump to the far-right end).
    const slots = board.querySelectorAll<HTMLElement>('[data-instance-id]');
    board.insertBefore(ghost, slots[boardIndex] ?? null);
    return ghost;
  }

  private rememberCards(state: CardDuelStateMessage): void {
    for (const card of [...state.you.board, ...state.foe.board]) {
      this.identities.set(card.instanceId, { cardId: card.cardId, rarity: card.rarity });
    }
  }

  private playMonsterSound(
    identity: CardBattleSoundIdentity | undefined,
    role: 'attack' | 'hurt' | 'die',
    volume?: number,
  ): void {
    const cue = cardMonsterCue(identity, role, volume);
    if (cue) this.onSound?.(cue);
  }

  private playEffectSounds(beat: WireCardBattleEffectBeat): void {
    const signature = cardEffectCue(beat);
    if (signature) {
      const source = beat.sourceInstanceId ?? beat.sourceCardId ?? 'system';
      const key = `${source}:${beat.effect}:${beat.action ?? ''}:${signature.sound}`;
      if (!this.soundedSignatures.has(key)) {
        this.soundedSignatures.add(key);
        this.onSound?.(signature);
      }
    }

    const targetId = beat.target?.kind === 'minion'
      ? beat.target.instanceId
      : beat.instanceId;
    if (
      beat.effect === 'damage'
      && beat.target?.kind !== 'hero'
      && targetId !== undefined
      && !this.hasUpcomingDeath(targetId)
    ) {
      this.playMonsterSound(this.identities.get(targetId), 'hurt');
    } else if (beat.effect === 'death' && targetId !== undefined) {
      const identity = beat.cardId && beat.rarity
        ? { cardId: beat.cardId, rarity: beat.rarity }
        : this.identities.get(targetId);
      this.playMonsterSound(identity, 'die');
    }
  }

  /** A lethal damage beat is followed by death; do not stack hurt and die voices. */
  private hasUpcomingDeath(instanceId: number): boolean {
    for (const queued of this.queue) {
      if (queued.beat.kind === 'play' || queued.beat.kind === 'attack') return false;
      if (queued.beat.effect === 'turn-start') return false;
      if (
        queued.beat.effect === 'death'
        && (
          queued.beat.instanceId === instanceId
          || (
            queued.beat.target?.kind === 'minion'
            && queued.beat.target.instanceId === instanceId
          )
        )
      ) return true;
    }
    return false;
  }

  private stageActor(instanceId: number): void {
    if (this.stagedGhosts.has(instanceId)) return;
    const actor = this.battlefield.querySelector<HTMLElement>(selectorForInstance(instanceId));
    if (!actor) return;
    const ghost = actor.cloneNode(true) as HTMLElement;
    ghost.classList.remove('selected', 'targetable', 'ready');
    ghost.removeAttribute('tabindex');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.pointerEvents = 'none';
    const board = actor.closest('.card-battle-board');
    const boardIndex = board
      ? [...board.querySelectorAll<HTMLElement>('[data-instance-id]')].indexOf(actor)
      : 0;
    const side: ArenaSide = actor.dataset.side === 'you' ? 'you' : 'foe';
    this.stagedGhosts.set(instanceId, { ghost, boardIndex: Math.max(0, boardIndex), side });
  }

  private retain(...actors: HTMLElement[]): void {
    for (const actor of actors) {
      actor.dataset.battleRetain = 'true';
    }
  }

  private release(...actors: HTMLElement[]): void {
    for (const actor of actors) {
      delete actor.dataset.battleRetain;
      if (actor.dataset.battleGhost === 'true') actor.remove();
    }
  }

  private attackFrames(body: number, direction: number): CardBattleSpriteArt[] {
    for (const action of ['attack-a', 'attack-b', 'attack-c'] as const) {
      const frames = cachedCardBattleSpriteFrames(body, action, direction);
      if (frames.length > 0) return frames.slice(0, MAX_ACTION_FRAMES);
    }
    return [];
  }

  private async playTravelFrames(
    actor: HTMLElement,
    frames: CardBattleSpriteArt[],
    hue: number,
    scale: number,
    generation: number,
  ): Promise<void> {
    const count = Math.ceil(ADVANCE_MS / FRAME_MS);
    for (let i = 0; i < count; i += 1) {
      if (generation !== this.generation || !actor.isConnected) return;
      const frame = frames[i % frames.length];
      if (frame) this.paintFrame(actor, frame, hue, scale);
      await wait(FRAME_MS);
    }
  }

  private async playFrames(
    actor: HTMLElement,
    frames: CardBattleSpriteArt[],
    hue: number,
    scale: number,
    generation: number,
  ): Promise<void> {
    for (const frame of frames) {
      if (generation !== this.generation || !actor.isConnected) return;
      this.paintFrame(actor, frame, hue, scale);
      await wait(FRAME_MS);
    }
  }

  private paintFrame(
    actor: HTMLElement,
    frame: CardBattleSpriteArt | undefined,
    hue: number,
    scale: number,
  ): void {
    if (!frame) return;
    const art = actor.querySelector<HTMLElement>('.card-art');
    if (!art) return;
    // A fresh canvas per frame prevents a slow atlas load for an older action from
    // repainting over a newer frame after its promise resolves.
    const canvas = this.doc.createElement('canvas');
    canvas.className = 'card-art-sprite card-battle-animated-sprite';
    canvas.dataset.hue = String(hue);
    canvas.dataset.visualScale = String(scale);
    canvas.width = frame.width;
    canvas.height = frame.height;
    canvas.style.width = `${frame.width * scale}px`;
    canvas.style.height = `${frame.height * scale}px`;
    canvas.style.left = `calc(50% - ${frame.cx * scale}px)`;
    canvas.style.bottom = `${frame.cy * scale}px`;
    canvas.style.backgroundImage = `url("${frame.atlasUrl}")`;
    canvas.style.backgroundPosition = `${cropPosition(frame.x, frame.atlasWidth, frame.width)}% ${cropPosition(frame.y, frame.atlasHeight, frame.height)}%`;
    canvas.style.backgroundSize = `${frame.atlasWidth / frame.width * 100}% ${frame.atlasHeight / frame.height * 100}%`;
    art.replaceChildren(canvas);
    void paintHuedCardSprite(canvas, frame, hue);
  }

  private launchFireBreath(actor: HTMLElement, target: HTMLElement): HTMLElement {
    const projectile = this.doc.createElement('span');
    projectile.className = 'card-battle-fire-breath';
    const fireball = cardActionArt('fireball');
    if (fireball) {
      projectile.style.backgroundImage = `url("${fireball.atlasUrl}")`;
      projectile.style.backgroundPosition = `${cropPosition(fireball.x, fireball.atlasWidth, fireball.width)}% ${cropPosition(fireball.y, fireball.atlasHeight, fireball.height)}%`;
      projectile.style.backgroundSize = `${fireball.atlasWidth / fireball.width * 100}% ${fireball.atlasHeight / fireball.height * 100}%`;
    }
    const arena = this.battlefield.getBoundingClientRect();
    const from = actor.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    projectile.style.setProperty('--breath-from-x', `${from.left + from.width / 2 - arena.left}px`);
    projectile.style.setProperty('--breath-from-y', `${from.top + from.height / 2 - arena.top}px`);
    projectile.style.setProperty('--breath-to-x', `${to.left + to.width / 2 - arena.left}px`);
    projectile.style.setProperty('--breath-to-y', `${to.top + to.height / 2 - arena.top}px`);
    this.battlefield.appendChild(projectile);
    return projectile;
  }
}
